import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../i18n/context';
import { STOCKS, fetchStockDetail } from '../utils/stockFundamentals';
import './StockDetailPage.css';
import '../styles/pro-page.css';

const hasValue = (value) => value !== null && value !== undefined && value !== '';

function fmtB(value) {
  if (!hasValue(value)) return '';
  return `$${Number(value).toFixed(2)}B`;
}

function fmtMoney(value) {
  if (!hasValue(value)) return '';
  return `$${Number(value).toFixed(2)}`;
}

function fmtMarketCap(value) {
  if (!hasValue(value)) return '';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(0)}B`;
  return `$${(value / 1e6).toFixed(0)}M`;
}

function fmtShares(value) {
  if (!hasValue(value)) return '';
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  return Number(value).toLocaleString();
}

function fmtPct(value, digits = 1) {
  if (!hasValue(value)) return '';
  return `${value >= 0 ? '+' : ''}${Number(value).toFixed(digits)}%`;
}

function fmtPlainPct(value, digits = 2) {
  if (!hasValue(value)) return '';
  return `${Number(value).toFixed(digits)}%`;
}

function fmtRatio(value) {
  if (!hasValue(value)) return '';
  return Number(value).toFixed(1);
}

function DataItem({ label, value, accent }) {
  if (!hasValue(value)) return null;
  return (
    <div className="stock-detail-data-item">
      <span className="stock-detail-data-label">{label}</span>
      <span className={`stock-detail-data-value${accent ? ' accent' : ''}`}>{value}</span>
    </div>
  );
}

export default function StockDetailPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const stock = STOCKS.find((item) => item.ticker === ticker);

  useEffect(() => {
    if (!stock) {
      setError(true);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchStockDetail(ticker)
      .then((detail) => {
        if (cancelled) return;
        if (detail) setData(detail);
        else setError(true);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticker, stock]);

  if (loading) {
    return (
      <main className="pro-page">
        <div className="pro-page-inner">
          <div className="stock-detail-loading">{t('home.cryptoDetailLoading')}</div>
        </div>
      </main>
    );
  }

  if (error || !stock || !data) {
    return (
      <main className="pro-page">
        <div className="pro-page-inner">
          <button className="pro-back" onClick={() => navigate(-1)}>{t('home.cryptoDetailBack')}</button>
          <div className="stock-detail-not-found">{t('home.cryptoDetailNotFound')}</div>
        </div>
      </main>
    );
  }

  const ratingLabel = data.rating === 'buy' ? t('home.stockFundRatingBuy')
    : data.rating === 'hold' ? t('home.stockFundRatingHold')
    : t('home.stockFundRatingWatch');

  const grossMargin = data.grossProfit && data.revenue ? (data.grossProfit / data.revenue) * 100 : null;
  const opMargin = data.operatingIncome && data.revenue ? (data.operatingIncome / data.revenue) * 100 : null;
  const netMargin = data.netIncome && data.revenue ? (data.netIncome / data.revenue) * 100 : null;

  const metricCards = [
    {
      key: 'revenue',
      label: t('home.stockFundRevenue'),
      value: fmtB(data.revenue),
      accent: true,
      sub: hasValue(data.revenueGrowth) ? `${fmtPct(data.revenueGrowth)} YoY` : null,
      subClass: data.revenueGrowth >= 0 ? 'up' : 'down',
    },
    {
      key: 'netIncome',
      label: t('home.stockFundNetIncome'),
      value: hasValue(data.netIncome) ? `${data.netIncome >= 0 ? '+' : ''}${fmtB(data.netIncome)}` : '',
      valueClass: data.netIncome < 0 ? ' neg' : '',
      sub: hasValue(netMargin) ? `${lang === 'zh' ? '净利率' : 'Net Margin'} ${fmtPlainPct(netMargin, 1)}` : null,
    },
    {
      key: 'marketCap',
      label: lang === 'zh' ? '市值' : 'Market Cap',
      value: fmtMarketCap(data.marketCap),
      accent: true,
    },
    {
      key: 'eps',
      label: lang === 'zh' ? '每股收益 EPS' : 'EPS',
      value: fmtMoney(data.eps),
    },
    {
      key: 'assets',
      label: lang === 'zh' ? '总资产' : 'Total Assets',
      value: fmtB(data.totalAssets),
    },
    {
      key: 'pe',
      label: 'P/E',
      value: fmtRatio(data.trailingPE),
    },
  ].filter((item) => hasValue(item.value));

  const details = [
    { label: lang === 'zh' ? '毛利润' : 'Gross Profit', value: fmtB(data.grossProfit) },
    { label: lang === 'zh' ? '毛利率' : 'Gross Margin', value: hasValue(grossMargin) ? fmtPlainPct(grossMargin, 1) : '' },
    { label: lang === 'zh' ? '营业利润' : 'Operating Income', value: fmtB(data.operatingIncome) },
    { label: lang === 'zh' ? '营业利润率' : 'Op Margin', value: hasValue(opMargin) ? fmtPlainPct(opMargin, 1) : '' },
    { label: lang === 'zh' ? '经营现金流' : 'Operating Cash Flow', value: fmtB(data.cashFlow) },
    { label: lang === 'zh' ? '流通股数' : 'Shares Outstanding', value: fmtShares(data.sharesOut) },
    { label: lang === 'zh' ? '52周最高' : '52W High', value: fmtMoney(data.fiftyTwoWeekHigh) },
    { label: lang === 'zh' ? '52周最低' : '52W Low', value: fmtMoney(data.fiftyTwoWeekLow) },
    {
      label: lang === 'zh' ? '股息率' : 'Dividend Yield',
      value: hasValue(data.dividendYield) ? fmtPlainPct(data.dividendYield * 100, 2) : '',
    },
    { label: lang === 'zh' ? '营收季度末' : 'Revenue Period End', value: data.revenueEnd },
    { label: lang === 'zh' ? '下一财报日' : 'Next Earnings', value: data.nextEarnings, accent: true },
  ].filter((item) => hasValue(item.value));

  return (
    <main className="pro-page">
      <div className="pro-page-inner">
        <button className="pro-back" onClick={() => navigate(-1)}>{t('home.cryptoDetailBack')}</button>

        <div className="stock-detail-header">
        <img src={stock.logo} alt="" className="stock-detail-logo" />
        <div className="stock-detail-header-info">
          <div className="stock-detail-header-name">
            {lang === 'zh' ? stock.nameCn : stock.name}
            <span className="stock-detail-header-ticker">{stock.ticker}</span>
          </div>
          <div className="stock-detail-header-sub">
            {lang === 'zh' ? '美国上市公司' : 'US Listed Company'}
            {data.nextEarnings && <> · {lang === 'zh' ? '下一财报日' : 'Next Earnings'}: {data.nextEarnings}</>}
          </div>
        </div>
        <div className="stock-detail-header-rating-wrap">
          <span className={`stock-detail-rating stock-detail-rating-${data.rating}`}>{ratingLabel}</span>
        </div>
      </div>

      {metricCards.length > 0 && (
        <div className="stock-detail-metrics">
          {metricCards.map((metric) => (
            <div key={metric.key} className="stock-detail-metric-card">
              <div className="stock-detail-metric-label">{metric.label}</div>
              <div className={`stock-detail-metric-value${metric.accent ? ' accent' : ''}${metric.valueClass || ''}`}>
                {metric.value}
              </div>
              {metric.sub && (
                <div className={`stock-detail-metric-change ${metric.subClass || ''}`}>{metric.sub}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="stock-detail-body">
        <div className="stock-detail-main">
          {details.length > 0 && (
            <div className="stock-detail-card">
              <div className="stock-detail-card-title">{lang === 'zh' ? '财务数据详情' : 'Financial Details'}</div>
              <div className="stock-detail-data-grid">
                {details.map((item) => (
                  <DataItem key={item.label} label={item.label} value={item.value} accent={item.accent} />
                ))}
              </div>
            </div>
          )}

          <div className="stock-detail-tools">
            <div className="stock-detail-tool-card" onClick={() => navigate('/pattern-match')}>
              <div className="stock-detail-tool-icon">K</div>
              <div className="stock-detail-tool-name">{lang === 'zh' ? 'K线形态' : 'Kline Pattern'}</div>
            </div>
            <div className="stock-detail-tool-card" onClick={() => navigate('/support-resistance')}>
              <div className="stock-detail-tool-icon">SR</div>
              <div className="stock-detail-tool-name">{lang === 'zh' ? '支撑压力位' : 'Support/Resistance'}</div>
            </div>
            <div className="stock-detail-tool-card" onClick={() => navigate('/dump-detective')}>
              <div className="stock-detail-tool-icon">DD</div>
              <div className="stock-detail-tool-name">{lang === 'zh' ? '出货侦探' : 'Dump Detective'}</div>
            </div>
          </div>
        </div>

        <div className="stock-detail-sidebar">
          <div className="stock-detail-sidebar-box">
            <div className="stock-detail-sidebar-title">{lang === 'zh' ? '公司信息' : 'Company Info'}</div>
            <div className="stock-detail-sidebar-rows">
              <div className="stock-detail-sidebar-row">
                <span className="label">{lang === 'zh' ? '公司' : 'Company'}</span>
                <span className="value">{stock.name}</span>
              </div>
              <div className="stock-detail-sidebar-row">
                <span className="label">{lang === 'zh' ? '代码' : 'Ticker'}</span>
                <span className="value accent">{stock.ticker}</span>
              </div>
              <div className="stock-detail-sidebar-row">
                <span className="label">CIK</span>
                <span className="value">{stock.cik}</span>
              </div>
              <div className="stock-detail-sidebar-row">
                <span className="label">{lang === 'zh' ? '评级' : 'Rating'}</span>
                <span className={`value stock-detail-rating-${data.rating}`}>{ratingLabel}</span>
              </div>
            </div>
          </div>
          <div className="stock-detail-sidebar-box">
            <div className="stock-detail-sidebar-title">SEC EDGAR</div>
            <a
              className="stock-detail-sidebar-link"
              href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${stock.cik}&type=10-&dateb=&owner=include&count=5`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {lang === 'zh' ? '查看 SEC 报告 ->' : 'View SEC Filings ->'}
            </a>
          </div>
        </div>
      </div>
      </div>
    </main>
  );
}
